import './App.css';
import React from "react";
import Box from '@mui/material/Box';
import {Alert, CircularProgress} from "@mui/material";
import {createTheme, ThemeProvider} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {useNavigate} from "react-router-dom";
import ApiCmsCtrl from "./api/ApiCmsCtrl";
import {AuthProvider, useAuth} from "./context/AuthContext";
import {AppProvider} from "./context/AppContext";
import {ToastProvider} from "./context/ToastContext";
import {ThemeToggleProvider, ThemeContext, PALETTES} from "./context/ThemeContext";
import BaBarre from "./components/layout/BaBarre";
import RouteContainer from "./pages/RouteContainer";
import dayjs from "dayjs";

export const APP_VERSION = "2.0.0";
export const APP_NAME = "promise";

/** Contenu applicatif, monté une fois config chargé */
function AppContent({ config }) {
    return (
        <ThemeToggleProvider>
            <AppContentInner config={config} />
        </ThemeToggleProvider>
    );
}

/** Niveau interne : accède au ThemeContext pour créer le thème MUI */
function AppContentInner({ config }) {
    const { paletteMode, darkMode } = React.useContext(ThemeContext);

    const theme = React.useMemo(() => {
        const palette = PALETTES[paletteMode];
        const paletteColors = palette?.colors ?? { primary: { main: config?.color || '#1976d2' } };
        return createTheme({
            cssVariables: false,
            palette: {
                mode: darkMode ? "dark" : "light",
                ...paletteColors,
            },
        });
    }, [config?.color, paletteMode, darkMode]);

    // Force le dark mode sur le document pour contourner les problèmes
    // d'ordre d'injection CSS en production (CRA + Emotion + MUI v7)
    React.useEffect(() => {
        const bg = theme.palette.background.default;
        const fg = theme.palette.text.primary;
        document.body.style.backgroundColor = bg;
        document.body.style.color = fg;
        document.documentElement.style.colorScheme = darkMode ? "dark" : "light";
    }, [theme, darkMode]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline enableColorScheme />
            <AuthProvider>
                <AppProvider config={config}>
                    <ToastProvider>
                        <AppShell />
                    </ToastProvider>
                </AppProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

function AppShell() {
    const { user, disconnect } = useAuth();
    const navigate = useNavigate();

    function handleDisconnect() {
        disconnect();
        navigate("/");
    }

    return (
        <Box sx={{ flexGrow: 1, width: '100%', minHeight: '100vh', bgcolor: 'background.default' }}>
            <BaBarre user={user} disconnect={handleDisconnect} />
            <RouteContainer />
        </Box>
    );
}

export default function App() {
    const [config, setConfig] = React.useState(null);
    const [cmsError, setCmsError] = React.useState(false);

    React.useEffect(() => {
        if (config && process.env.NODE_ENV !== 'production') {
            console.log("config", config);
        }
    }, [config]);

    React.useEffect(() => {
        async function loadConfig() {
            const { data, error } = await ApiCmsCtrl.getCmsTemplate();
            if (data) setConfig(data);
            else if (error) setCmsError(true);
        }
        loadConfig();
    }, []);



    if (cmsError) {
        return (
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
                <Alert severity="error" sx={{ maxWidth: 500 }}>
                    Impossible de charger la configuration de l'application. Veuillez vérifier votre connexion et recharger la page.
                </Alert>
            </Box>
        );
    }

    if (!config) {
        return (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return <AppContent config={config} />;
}
